import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Container,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Paper,
  Button,
  Divider,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import "./i18n";

function App() {
  const { t, i18n } = useTranslation();
  const [device, setDevice] = useState("");
  const [browser, setBrowser] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Read language from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get("lang");

    if (langParam && (langParam === "en" || langParam === "ar")) {
      i18n.changeLanguage(langParam);
      document.documentElement.dir = langParam === "ar" ? "rtl" : "ltr";
    }
  }, [i18n]);

  // Detect device and browser from user agent
  useEffect(() => {
    const ua = navigator.userAgent;

    // Detect device
    if (ua.includes("iPhone") || ua.includes("iPad") || ua.includes("iPod")) {
      setDevice("ios");
    } else if (ua.includes("Android")) {
      setDevice("android");
    }

    // Detect browser
    if (ua.includes("Chrome")) {
      setBrowser("chrome");
    } else if (ua.includes("Safari") && !ua.includes("Chrome")) {
      setBrowser("safari");
    } else if (ua.includes("Firefox")) {
      setBrowser("firefox");
    } else if (ua.includes("SamsungBrowser")) {
      setBrowser("samsungInternet");
    }
  }, []);

  const handleDeviceChange = (event) => {
    setDevice(event.target.value);
    setBrowser(""); // Reset browser when device changes
  };

  const handleBrowserChange = (event) => {
    setBrowser(event.target.value);
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === "en" ? "ar" : "en";
    i18n.changeLanguage(newLang);
    document.documentElement.dir = newLang === "ar" ? "rtl" : "ltr";

    // Update URL parameter without reloading the page
    const url = new URL(window.location.href);
    url.searchParams.set("lang", newLang);
    window.history.pushState({}, "", url);
  };

  const getInstructions = () => {
    if (!device || !browser) return "";
    return t(`deviceInstructions.${device}.${browser}`);
  };

  const getDeviceEmoji = () => {
    return device === "ios" ? (
      "📱"
    ) : device === "android" ? (
      <img
        src="/images/android.svg"
        alt="Android"
        style={{ width: "1.2em", height: "1.2em", verticalAlign: "middle" }}
      />
    ) : (
      "❓"
    );
  };

  const getBrowserEmoji = () => {
    switch (browser) {
      case "chrome":
        return (
          <img
            src="/images/chrome.svg"
            alt="Chrome"
            style={{ width: "1.2em", height: "1.2em", verticalAlign: "middle" }}
          />
        );
      case "safari":
        return "🧭";
      case "firefox":
        return (
          <img
            src="/images/firefox.png"
            alt="Firefox"
            style={{ width: "1.2em", height: "1.2em", verticalAlign: "middle" }}
          />
        );
      case "samsungInternet":
        return "📱";
      default:
        return "❓";
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: isMobile ? 2 : 4 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: 4,
          gap: isMobile ? 2 : 0,
          dir: i18n.language === "ar" ? "rtl" : "ltr",
        }}
      >
        <Box sx={{ order: i18n.language === "ar" ? 2 : 1 }}>
          <Typography
            variant={isMobile ? "h5" : "h4"}
            component="h1"
            gutterBottom
          >
            {t("title")} 📍
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {device && browser ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  dir: i18n.language === "ar" ? "rtl" : "ltr",
                }}
              >
                {getDeviceEmoji()} {t(device)} + {getBrowserEmoji()}{" "}
                {t(browser)}
              </Box>
            ) : (
              t("selectPrompt")
            )}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={toggleLanguage}
          startIcon={i18n.language === "en" ? "🌐" : "🌍"}
          size={isMobile ? "small" : "medium"}
          sx={{ order: i18n.language === "ar" ? 1 : 2 }}
        >
          {i18n.language === "en" ? "العربية" : "English"}
        </Button>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: isMobile ? 2 : 3,
          border: "1px solid",
          borderColor: "divider",
          dir: i18n.language === "ar" ? "rtl" : "ltr",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 2,
            mb: 3,
            dir: i18n.language === "ar" ? "rtl" : "ltr",
          }}
        >
          <FormControl fullWidth dir={i18n.language === "ar" ? "rtl" : "ltr"}>
            <InputLabel>{t("selectDevice")}</InputLabel>
            <Select
              value={device}
              label={t("selectDevice")}
              onChange={handleDeviceChange}
              size={isMobile ? "small" : "medium"}
            >
              <MenuItem value="ios">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    dir: i18n.language === "ar" ? "rtl" : "ltr",
                  }}
                >
                  <span>📱</span> {t("ios")}
                </Box>
              </MenuItem>
              <MenuItem value="android">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    dir: i18n.language === "ar" ? "rtl" : "ltr",
                  }}
                >
                  <img
                    src="/images/android.svg"
                    alt="Android"
                    style={{
                      width: "1.2em",
                      height: "1.2em",
                      verticalAlign: "middle",
                    }}
                  />
                  {t("android")}
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth dir={i18n.language === "ar" ? "rtl" : "ltr"}>
            <InputLabel>{t("selectBrowser")}</InputLabel>
            <Select
              value={browser}
              label={t("selectBrowser")}
              onChange={handleBrowserChange}
              disabled={!device}
              size={isMobile ? "small" : "medium"}
            >
              {device === "ios"
                ? [
                    <MenuItem key="chrome" value="chrome">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          dir: i18n.language === "ar" ? "rtl" : "ltr",
                        }}
                      >
                        <img
                          src="/images/chrome.svg"
                          alt="Chrome"
                          style={{
                            width: "1.2em",
                            height: "1.2em",
                            verticalAlign: "middle",
                          }}
                        />
                        {t("chrome")}
                      </Box>
                    </MenuItem>,
                    <MenuItem key="safari" value="safari">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          dir: i18n.language === "ar" ? "rtl" : "ltr",
                        }}
                      >
                        <span>🧭</span> {t("safari")}
                      </Box>
                    </MenuItem>,
                    <MenuItem key="firefox" value="firefox">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          dir: i18n.language === "ar" ? "rtl" : "ltr",
                        }}
                      >
                        <img
                          src="/images/firefox.png"
                          alt="Firefox"
                          style={{
                            width: "1.2em",
                            height: "1.2em",
                            verticalAlign: "middle",
                          }}
                        />
                        {t("firefox")}
                      </Box>
                    </MenuItem>,
                  ]
                : [
                    <MenuItem key="chrome" value="chrome">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          dir: i18n.language === "ar" ? "rtl" : "ltr",
                        }}
                      >
                        <img
                          src="/images/chrome.svg"
                          alt="Chrome"
                          style={{
                            width: "1.2em",
                            height: "1.2em",
                            verticalAlign: "middle",
                          }}
                        />
                        {t("chrome")}
                      </Box>
                    </MenuItem>,
                    <MenuItem key="samsungInternet" value="samsungInternet">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          dir: i18n.language === "ar" ? "rtl" : "ltr",
                        }}
                      >
                        <span>📱</span> {t("samsungInternet")}
                      </Box>
                    </MenuItem>,
                    <MenuItem key="firefox" value="firefox">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          dir: i18n.language === "ar" ? "rtl" : "ltr",
                        }}
                      >
                        <img
                          src="/images/firefox.png"
                          alt="Firefox"
                          style={{
                            width: "1.2em",
                            height: "1.2em",
                            verticalAlign: "middle",
                          }}
                        />
                        {t("firefox")}
                      </Box>
                    </MenuItem>,
                  ]}
            </Select>
          </FormControl>
        </Box>

        {device && browser && (
          <Paper
            variant="outlined"
            sx={{
              p: isMobile ? 2 : 3,
              bgcolor: "background.default",
              border: "1px solid",
              borderColor: "divider",
              dir: i18n.language === "ar" ? "rtl" : "ltr",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
                flexWrap: "wrap",
                dir: i18n.language === "ar" ? "rtl" : "ltr",
              }}
            >
              <Typography variant="h6" component="h2">
                {t("instructions")}
              </Typography>
              <Chip
                icon={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      dir: i18n.language === "ar" ? "rtl" : "ltr",
                    }}
                  >
                    {getDeviceEmoji()} {getBrowserEmoji()}
                  </Box>
                }
                label=""
                size="small"
                sx={{
                  ml: i18n.language === "ar" ? 0 : 1,
                  mr: i18n.language === "ar" ? 1 : 0,
                }}
              />
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography
              component="pre"
              sx={{
                whiteSpace: "pre-wrap",
                fontFamily: "inherit",
                fontSize: isMobile ? "0.9rem" : "1rem",
                lineHeight: 1.6,
                color: "text.secondary",
                overflowX: "auto",
                dir: i18n.language === "ar" ? "rtl" : "ltr",
                textAlign: i18n.language === "ar" ? "right" : "left",
              }}
            >
              {getInstructions()}
            </Typography>
          </Paper>
        )}
      </Paper>
    </Container>
  );
}

export default App;
